import { useState } from 'react';
import { AlertCircleIcon, AlertTriangleIcon, XIcon } from 'lucide-react';
import type { QuotaMeStatusDTO } from '@shared/schemas/quota';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { formatQuotaDate, formatTokenCount } from '../quota-format';
import { getQuotaBannerState } from '../quota-banner-state';
import { getEffectiveQuotaConstraint } from '../quota-constraint';

const WARNING_DISMISSAL_STORAGE_KEY = 'quota-warning-dismissed-renews-at';

function readDismissedRenewsAt() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage.getItem(WARNING_DISMISSAL_STORAGE_KEY);
}

export function QuotaStatusBanner({
  canManageCompany = false,
  status,
}: {
  canManageCompany?: boolean;
  status: QuotaMeStatusDTO | undefined;
}) {
  const [dismissedRenewsAt, setDismissedRenewsAt] = useState<string | null>(readDismissedRenewsAt);
  const bannerState = getQuotaBannerState(status, dismissedRenewsAt);

  if (!status || bannerState === 'hidden') {
    return null;
  }

  const constraint = getEffectiveQuotaConstraint(status);

  if (bannerState === 'blocked') {
    if (constraint.source === 'company') {
      return (
        <Alert variant="destructive" data-testid="company-quota-blocked-banner">
          <AlertCircleIcon />
          <AlertTitle>Limite de l’entreprise atteinte</AlertTitle>
          <AlertDescription>
            {canManageCompany
              ? 'Le quota de votre entreprise est épuisé. Ajoutez des tokens ou ajustez la limite depuis la console d’entreprise.'
              : 'Le quota de votre entreprise est épuisé. Contactez un administrateur de votre entreprise.'}{' '}
            Réinitialisation le {formatQuotaDate(constraint.resetAt)}.
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <Alert variant="destructive" data-testid="quota-blocked-banner">
        <AlertCircleIcon />
        <AlertTitle>Limite du compte démo atteinte</AlertTitle>
        <AlertDescription>
          Vous avez atteint la limite de votre compte démo. Votre quota sera réinitialisé le{' '}
          {formatQuotaDate(constraint.resetAt)}. Contactez votre administrateur pour obtenir du quota
          supplémentaire.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert data-testid="quota-warning-banner">
      <AlertTriangleIcon />
      <AlertTitle>Vous approchez de votre limite</AlertTitle>
      <AlertDescription>
        Vous approchez de la limite {constraint.source === 'company' ? 'de votre entreprise' : 'de votre compte démo'} ({formatTokenCount(constraint.tokensRemaining)}{' '}
        tokens restants). Réinitialisation le {formatQuotaDate(constraint.resetAt)}.
      </AlertDescription>
      <AlertAction>
        <Button
          aria-label="Masquer l’avertissement de quota"
          onClick={() => {
            const renewalKey = constraint.resetAt ?? '';
            window.sessionStorage.setItem(WARNING_DISMISSAL_STORAGE_KEY, renewalKey);
            setDismissedRenewsAt(renewalKey);
          }}
          size="icon-sm"
          type="button"
          variant="ghost"
        >
          <XIcon />
        </Button>
      </AlertAction>
    </Alert>
  );
}
